import { faCalendarDays, faHandHoldingDollar, faMoneyBill, faShare } from '@fortawesome/pro-light-svg-icons'
import { useState, useMemo, useCallback, useEffect } from 'react'
import { haveModuleBuyed } from '../../../utils/globals'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { getEmployeeParams, getAllGroupPermissions, getModulesBuyed } from '../../../utils/reducers/getters'
import { changeEmployeeParams } from '../../../utils/reducers/reduxDispatch'
import { getMaxPermissionByGroupPermissions } from '../../CreateRoutering/functions'
import DSBSueBar from '../Components/DSBSueBar'
import DSBSueCircle, { DSBSueCircleExpired } from '../Components/DSBSueCircle'
import HolidayCalendar from '../Components/HolidayCalendar'
import LastEmployeeTimetable from '../Components/LastEmployeeTimetable'
import WeekHolidayTable from '../Components/WeekHolidayTable'
import { DashboardWidgetAndFamiliesExtraComplete, UserWidget, WidgetDictionary } from './types'
import DSBAllTasksTable from '../Components/DSBAllTasksTable'
import moment from 'moment'
import DSBMyTasksTable from '../Components/DSBMyTasksTable'
import DSBQuoteAmount from '../Components/DSBQuoteAmount'
import DSBQuoteSellers from '../Components/DSBQuoteSellers'
import DSBQuoteMultipleChartData from '../Components/DSBQuoteMultipleChartData'
import DSBQuoteTotalVsAcceptedDays from '../Components/DSBQuoteAcceptedVsTotalDays'
import DSBQuoteStatusProgressBar from '../Components/DSBQuoteStatusProgressBar'
import DSBQuoteStatusPercentage from '../Components/DSBQuoteStatusPercentage'
import DSBMyCampaignsTable from '../Components/DSBMyCampaignsTable'
import DSBAllCampaignsTable from '../Components/DSBAllCampaignsTable'
import DSBMyCustomerProjectsTable from '../Components/DSBMyCustomerProjectsTable'
import DSBAllCustomerProjectsTable from '../Components/DSBAllCustomerProjectsTable'
import DSBMyTasksOverdueTable from '../Components/DSBMyTasksOverdueTable'
import DSBAllTasksOverdueTable from '../Components/DSBAllTasksOverdueTable'
import { QuoteOrderStatusInversed } from '@teinor/erp/types/company/order/quote/quoteOrder'
import DSBSellInvoiceStatusPercentage from '../Components/DSBSellInvoiceStatusPercentage'
import DSBSellInvoiceStatusProgressBar from '../Components/DSBSellInvoiceStatusProgressBar'
import DSBSellInvoiceSellers from '../Components/DSBSellInvoiceSellers'
import { sellInvoiceStatusInversed } from '@teinor/erp/types/company/order/sell/sellInvoice'
import DSBSellInvoiceMultipleChartData from '../Components/DSBSellInvoiceMultipleChartData'
import DSBSellInvoiceTotalVsAcceptedDays from '../Components/DSBSellInvoiceAcceptedVsTotalDays'
import DSBSellInvoiceAmount from '../Components/DSBSellInvoiceAmount'
import { projectTypesInversed } from '@teinor/erp/types/company/customer/customerProject'
import { sellTicketStatusInversed } from '@teinor/erp/types/company/order/sell/sellTicket'
import DSBSellTicketStatusPercentage from '../Components/DSBSellTicketStatusPercentage'
import DSBSellTicketAmount from '../Components/DSBSellTicketAmount'
import DSBSellTicketMultipleChartData from '../Components/DSBSellTicketMultipleChartData'
import DSBSellTicketSellers from '../Components/DSBSellTicketSellers'
import DSBSellTicketTotalVsAcceptedDays from '../Components/DSBSellTicketTotalVsAcceptedDays'
import DSBSellTicketStatusProgressBar from '../Components/DSBSellTicketStatusProgressBar'

export interface UseDashboardWidgetsProps {
    name: string
    defaultWidgets: string
}
const useDashboardWidgets = ({ name, defaultWidgets }: UseDashboardWidgetsProps): DashboardWidgetAndFamiliesExtraComplete => {
    const { t } = useTwinTranslation()
    const [userDashboardWidgets, setUserDashboardWidgets] = useState<UserWidget[]>([])
    const DSBWidgetTitlesTranslatedParsed = JSON.stringify({
        sue_bar_chart: t('dsbSueStatusChart', 'Gráfico de denuncias'),
        sue_status_informed: t('dsbSueWithoutProcessingTitle', 'Denuncias Sin procesar'),
        sue_status_closed: t('dsbSueClosedTitle', 'Denuncias Cerradas'),
        sue_status_processed: t('dsbSueInProcessTitle', 'Denuncias En proceso'),
        sue_status_expired: t('dsbSueExpiredTitle', 'Denuncias Fuera de plazo'),
        holiday_calendar_employees: t('holidayCalendar', 'Calendario de vacaciones'),
        quote_months_totalvsaccepted: t('montlyQuotes', 'Presupuestos por meses'),
        quote_pending_amount: t('pending', 'Pendiente'),
        quote_accepted_amount: t('accepted', 'Aceptado'),
        quote_denied_amount: t('quoteCancelledAmountShort', 'Imp. cancelados'),
        quote_sent_amount: t('sent', 'Enviados'),
        quote_draft_amount: t('quoteDraftAmountShort', 'Imp. en borrador'),
        quote_accepted_seller: t('quoteAcceptedSeller', 'Presupuestos aceptados por vendedor'),
        quote_denied_seller: t('quoteDeniedSeller', 'Presupuestos denegados por vendedor'),
        quote_multiplechart_data: t('quotesByMonthsComplete', 'Presupuestos por mes completo'),
        quote_days_totalvsaccepted: t('dailyQuotesThreeMonths', 'Presupuestos por días (Últimos tres meses)'),
        quote_status_progress_bar: t('quoteStatusMonthlyBar', 'Estado de los presupuestos (mes)'),
        quote_status_monthly: t('quoteStatusMonthly', 'Estado de los presupuestos (mes/%)'),
        sell_invoice_months_totalvsaccepted: t('montlySellInvoices', 'Facturas venta por meses'),
        sell_invoice_accepted_amount: t('sellInvoicesAccepted', 'Facturas aceptadas'),
        sell_invoice_draft_amount: t('sellInvoicesDraft', 'Facturas en borrador'),
        sell_invoice_accepted_seller: t('sellInvoicesAcceptedSeller', 'Facturas de venta aceptadas por vendedor'),
        sell_invoice_multiplechart_data: t('sellInvoicesByMonthsComplete', 'Facturas de venta por mes completo'),
        sell_invoice_days_totalvsaccepted: t('dailySellInvoicesThreeMonths', 'Facturas de venta por días (Últimos tres meses)'),
        sell_invoice_status_progress_bar: t('sellInvoiceStatusMonthlyBar', 'Estado de las facturas de venta (mes)'),
        sell_invoice_status_monthly: t('sellInvoiceStatusMonthly', 'Estado de las facturas de venta (mes/%)'),
        sell_ticket_months_totalvsaccepted: t('montlyTicket', 'Tickets de venta por meses'),
        sell_ticket_accepted_amount: t('allTicketsAccepted', 'Tickets aceptados'),
        sell_ticket_saved_amount: t('allTicketsSaved', 'Tickets reservados'),
        sell_ticket_canceled_amount: t('allTicketsCanceled', 'Tickets cancelados'),
        sell_ticket_refunded_amount: t('allTicketsRefunded', 'Tickets devueltos'),
        sell_ticket_accepted_seller: t('allTicketsAcceptedSeller', 'Tickets de venta aceptados por vendedor'),
        sell_ticket_multiplechart_data: t('ticketsByMonthsComplete', 'Tickets de venta por mes completo'),
        sell_ticket_days_totalvsaccepted: t('dailyTicketsThreeMonths', 'Tickets de venta por días (Últimos tres meses)'),
        sell_ticket_status_progress_bar: t('ticketStatusMonthlyBar', 'Estado de los tickets de venta (mes)'),
        sell_ticket_status_monthly: t('ticketStatusMonthly', 'Estado de los tickets de venta (mes/%)'),
        holiday_table_employees_week: t('employeesWithHolidayThisWeekTable', 'Tabla de empleados con vacaciones esta semana'),
        lastest_employee_timetable: t('latestEmployeeEntriesTable', 'Tabla de las últimas entradas de empleados'),
        task_todays_tasks: t('dsbTaskTodaysTasksTitle', 'Tareas para hoy'),
        task_weeks_tasks: t('dsbTaskWeeksTasksTitle', 'Tareas para esta semana'),
        task_overdue_tasks: t('dsbTaskOverdueTasksTitle','Tareas atrasadas'),
        my_task_todays_tasks: t('dsbTaskTodaysMyTasksTitle', 'Mis tareas para hoy'),
        my_task_overdue_tasks: t('dsbTaskOverdueMyTasksTitle', 'Mis tareas atrasadas'),
        my_task_weeks_tasks: t('dsbTaskWeeksMyTasksTitle', 'Mis tareas para esta semana'),
        campaign_todays_campaigns: t('dsbTaskTodaysCampaignsTitle', 'Campañas para hoy'),
        campaign_weeks_campaigns: t('dsbTaskWeeksCampaignsTitle', 'Campañas para esta semana'),
        my_campaign_todays_campaigns: t('dsbTaskTodaysMyCampaignTitle', 'Mis campañas para hoy'),
        my_campaign_weeks_campaigns: t('dsbTaskWeeksMyCampaignTitle', 'Mis campañas para esta semana'),
        customer_project_todays_customer_projects: t('dsbTaskTodaysCustomerProjectsTitle', 'Proyectos para hoy'),
        customer_project_weeks_customer_projects: t('dsbTaskWeeksCustomerProjectsTitle', 'Proyectos para esta semana'),
        my_customer_project_todays_customer_projects: t('dsbTaskTodaysMyCustomerProjectsTitle', 'Mis proyectos para hoy'),
        my_customer_project_weeks_customer_projects: t('dsbTaskWeeksMyCustomerProjectsTitle', 'Mis proyectos para esta semana'),
        customer_opportunity_todays_customer_opportunities: t('dsbTaskTodaysCustomerOpportunitiesTitle', 'Oportunidades para hoy'),
        customer_opportunity_weeks_customer_opportunities: t('dsbTaskWeeksCustomerOpportunitiesTitle', 'Oportunidades para esta semana'),
        my_customer_opportunity_todays_customer_opportunities: t('dsbTaskTodaysMyCustomerOpportunitiesTitle', 'Mis oportunidades para hoy'),
        my_customer_opportunity_weeks_customer_opportunities: t('dsbTaskWeeksMyCustomerOpportunitiesTitle', 'Mis oportunidades para esta semana'),
    })

    const avaliableDashboardWidgets: WidgetDictionary = useMemo(() => {
        const DSBWidgetTitlesTranslated = JSON.parse(DSBWidgetTitlesTranslatedParsed)
        const utilsDates = {
            currentDate: moment().endOf('day').toDate(),
            todayStart: moment().startOf('day').toDate(),
            todayEnd: moment().endOf('day').toDate(),
            yesterdayEnd: moment().subtract(1,'day').endOf('day').toDate(),
            weekStart: moment().startOf('week').toDate(),
            weekEnd: moment().endOf('week').toDate(),
            monthStart: moment().startOf('month').toDate(),
            monthEnd: moment().endOf('month').toDate(),
            monthThree: moment().subtract(3, 'months').startOf('month'),
            yearStart: moment().startOf('year').toDate(),
            yearEnd: moment().endOf('year').toDate(),
            weekPreviousStart: moment().subtract(1, 'week').startOf('week').toDate(),
            weekPreviousEnd: moment().subtract(1, 'week').day(moment().day()).endOf('day').toDate(),
            monthPreviousStart: moment().subtract(1, 'month').startOf('month').toDate(),
            monthPreviousEnd: moment().subtract(1, 'month').date(moment().date()).endOf('day').toDate(),
            yearPreviousStart: moment().subtract(1, 'year').startOf('year').toDate(),
            yearPreviousEnd: moment().subtract(1, 'year').date(moment().date()).endOf('day').toDate()
        }

        return filterWidgetsByPermissions({
            'sue_bar_chart': {
                component: DSBSueBar,
                cols: 2,
                rows: 3,
                family: 'sue',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sue_bar_chart
                },
                permission: 'sue',
                modules: ['sue']
            },
            'sue_status_informed': {
                component: DSBSueCircle,
                cols: 1,
                rows: 2,
                family: 'sue',
                extraProps: {
                    status: 'informed',
                    title: DSBWidgetTitlesTranslated.sue_status_informed
                },
                permission: 'sue',
                modules: ['sue']
            },
            'sue_status_closed': {
                component: DSBSueCircle,
                cols: 1,
                rows: 2,
                family: 'sue',
                extraProps: {
                    status: 'closed',
                    title: DSBWidgetTitlesTranslated.sue_status_closed
                },
                permission: 'sue',
                modules: ['sue']
            },
            'sue_status_processed': {
                component: DSBSueCircle,
                cols: 1,
                rows: 2,
                family: 'sue',
                extraProps: {
                    status: 'processing',
                    title: DSBWidgetTitlesTranslated.sue_status_processed
                },
                permission: 'sue',
                modules: ['sue']
            },
            'sue_status_expired': {
                component: DSBSueCircleExpired,
                cols: 1,
                rows: 2,
                family: 'sue',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sue_status_expired
                },
                permission: 'sue',
                modules: ['sue']
            },
            'holiday_calendar_employees': {
                component: HolidayCalendar,
                cols: 2,
                rows: 4,
                family: 'working_calendar',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.holiday_calendar_employees
                },
                permission: 'WCal.holiday.usedDays',
                modules: ['working_calendar', 'working_calendar_medium', 'working_calendar_full']
            },
            'holiday_table_employees_week': {
                component: WeekHolidayTable,
                cols: 2,
                rows: 2,
                family: 'working_calendar',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.holiday_table_employees_week
                },
                permission: 'WCal.holiday.usedDays',
                modules: ['working_calendar_medium', 'working_calendar_full']
            },
            'lastest_employee_timetable': {
                component: LastEmployeeTimetable,
                cols: 2,
                rows: 2,
                family: 'working_calendar',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.lastest_employee_timetable
                },
                permission: 'WCal.checkEmployeeMonth',
                modules: ['working_calendar', 'working_calendar_medium', 'working_calendar_full']
            },
            'my_task_overdue_tasks': {
                component: DSBMyTasksOverdueTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_task_overdue_tasks,
                    yesterdayEnd: utilsDates.yesterdayEnd
                },
                permission: 'task.main',
                modules: ['tasks']
            },
            'my_task_todays_tasks': {
                component: DSBMyTasksTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_task_todays_tasks,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd
                },
                permission: 'task.main',
                modules: ['tasks']
            },
            'my_task_weeks_tasks': {
                component: DSBMyTasksTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_task_weeks_tasks,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd
                },
                permission: 'task.main',
                modules: ['tasks']
            },
            'task_overdue_tasks': {
                component: DSBAllTasksOverdueTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.task_overdue_tasks,
                    yesterdayEnd: utilsDates.yesterdayEnd
                },
                permission: 'task.main',
                modules: ['tasks']
            },
            'task_todays_tasks': {
                component: DSBAllTasksTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.task_todays_tasks,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd
                },
                permission: 'task.main',
                modules: ['tasks']
            },
            'task_weeks_tasks': {
                component: DSBAllTasksTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.task_weeks_tasks,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd
                },
                permission: 'task.main',
                modules: ['tasks']
            },
            'quote_status_percentage': {
                component: DSBQuoteStatusPercentage,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_status_monthly,
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'quote.stats',
                family: 'quote',
                modules: ['quotes']
            },
            'quote_pending_amount': {
                component: DSBQuoteAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_pending_amount,
                    icon: faHandHoldingDollar,
                    quoteStatus: [QuoteOrderStatusInversed['waitingResponse']],
                    color: '#FFA200',
                    dates: utilsDates,
                },
                permission: 'quote.stats',
                family: 'quote',
                modules: ['quotes']
            },
            'quote_accepted_amount': {
                component: DSBQuoteAmount,
                cols: 1,
                rows: 1,
                className: 'small_padding_widget',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_accepted_amount,
                    icon: faMoneyBill,
                    quoteStatus: [QuoteOrderStatusInversed['accepted'], QuoteOrderStatusInversed['processed']],
                    color: '#1DB27B',
                    dates: utilsDates,
                    start_date: utilsDates.monthStart,
                    end_date: utilsDates.monthEnd
                },
                permission: 'quote.stats',
                family: 'quote',
                modules: ['quotes']
            },
            'quote_denied_amount': {
                component: DSBQuoteAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_denied_amount,
                    icon: faCalendarDays,
                    quoteStatus: [QuoteOrderStatusInversed['denied']],
                    color: '#EB2D53',
                    dates: utilsDates,
                },
                permission: 'quote.stats',
                family: 'quote',
                modules: ['quotes']
            },
            'quote_sent_amount': {
                component: DSBQuoteAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_sent_amount,
                    icon: faShare,
                    quoteStatus: [QuoteOrderStatusInversed['sent']],
                    color: '#865FE5',
                    dates: utilsDates,
                },
                permission: 'quote.stats',
                family: 'quote',
                modules: ['quotes']
            },
            'quote_draft_amount': {
                component: DSBQuoteAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_draft_amount,
                    icon: faCalendarDays,
                    quoteStatus: [QuoteOrderStatusInversed['creating']],
                    color: '#C548FF',
                    dates: utilsDates,
                },
                permission: 'quote.stats',
                family: 'quote',
                modules: ['quotes']
            },
            'quote_accepted_sellers': {
                component: DSBQuoteSellers,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_accepted_seller,
                    quoteStatus: [QuoteOrderStatusInversed['accepted'], QuoteOrderStatusInversed['processed']],
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'quote.stats',
                family: 'quote',
                modules: ['quotes']
            },
            'quote_denied_sellers': {
                component: DSBQuoteSellers,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_denied_seller,
                    quoteStatus: [QuoteOrderStatusInversed['denied']],
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                family: 'quote',
                permission: 'quote.stats',
                modules: ['quotes']
            },
            'quote_multiple_chart_data': {
                component: DSBQuoteMultipleChartData,
                cols: 2,
                rows: 3,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_multiplechart_data,
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                family: 'quote',
                permission: 'quote.stats',
                modules: ['quotes']
            },
            'quote_days_totalvsaccepted': {
                component: DSBQuoteTotalVsAcceptedDays,
                cols: 2,
                rows: 3,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_days_totalvsaccepted,
                    start_date: utilsDates.monthThree,
                    end_date: utilsDates.todayEnd
                },
                family: 'quote',
                permission: 'quote.stats',
                modules: ['quotes']
            },
            'quote_status_progress_bar': {
                component: DSBQuoteStatusProgressBar,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.quote_status_progress_bar,
                    start_date: utilsDates.monthStart,
                    end_date: utilsDates.monthEnd
                },
                family: 'quote',
                permission: 'quote.stats',
                modules: ['quotes']
            },
            'sell_invoice_status_percentage': {
                component: DSBSellInvoiceStatusPercentage,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_invoice_status_monthly,
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'sell.stats',
                family: 'sell_invoices',
                modules: ['quotes']
            },
            'sell_invoice_accepted_amount': {
                component: DSBSellInvoiceAmount,
                cols: 1,
                rows: 1,
                className: 'small_padding_widget',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_invoice_accepted_amount,
                    icon: faMoneyBill,
                    sellInvoiceStatus: [sellInvoiceStatusInversed['accepted']],
                    color: '#1DB27B',
                    dates: utilsDates,
                    start_date: utilsDates.monthStart,
                    end_date: utilsDates.monthEnd
                },
                permission: 'sell.stats',
                family: 'sell_invoices',
                modules: ['quotes']
            },
            'sell_invoice_draft_amount': {
                component: DSBSellInvoiceAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_invoice_draft_amount,
                    icon: faCalendarDays,
                    sellInvoiceStatus: [sellInvoiceStatusInversed['creating']],
                    color: '#C548FF',
                    dates: utilsDates,
                },
                permission: 'sell.stats',
                family: 'sell_invoices',
                modules: ['quotes']
            },
            'sell_invoice_multiple_chart_data': {
                component: DSBSellInvoiceMultipleChartData,
                cols: 2,
                rows: 3,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_invoice_multiplechart_data,
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'sell.stats',
                family: 'sell_invoices',
                modules: ['quotes']
            },
            'sell_invoice_accepted_sellers': {
                component: DSBSellInvoiceSellers,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_invoice_accepted_seller,
                    sellInvoiceStatus: [sellInvoiceStatusInversed['accepted']],
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'sell.stats',
                family: 'sell_invoices',
                modules: ['quotes']
            },
            'sell_invocie_days_totalvsaccepted': {
                component: DSBSellInvoiceTotalVsAcceptedDays,
                cols: 2,
                rows: 3,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_invoice_days_totalvsaccepted,
                    start_date: utilsDates.monthThree,
                    end_date: utilsDates.todayEnd
                },
                family: 'sell_invoices',
                permission: 'sell.stats',
                modules: ['quotes']
            },
            'sell_invoice_status_progress_bar': {
                component: DSBSellInvoiceStatusProgressBar,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_invoice_status_progress_bar,
                    start_date: utilsDates.monthStart,
                    end_date: utilsDates.monthEnd
                },
                family: 'sell_invoices',
                permission: 'sell.stats',
                modules: ['quotes']
            },
            'sell_ticket_status_percentage': {
                component: DSBSellTicketStatusPercentage,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_status_monthly,
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'sell.stats',
                family: 'tpv',
                modules: ['quotes']
            },
            'sell_ticket_accepted_amount': {
                component: DSBSellTicketAmount,
                cols: 1,
                rows: 1,
                className: 'small_padding_widget',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_accepted_amount,
                    icon: faMoneyBill,
                    sellTicketStatus: [sellTicketStatusInversed['accepted']],
                    color: '#1DB27B',
                    dates: utilsDates,
                    start_date: utilsDates.monthStart,
                    end_date: utilsDates.monthEnd
                },
                permission: 'sell.stats',
                family: 'tpv',
                modules: ['quotes']
            },
            'sell_ticket_saved_amount': {
                component: DSBSellTicketAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_saved_amount,
                    icon: faCalendarDays,
                    sellTicketStatus: [sellTicketStatusInversed['saved']],
                    color: '#C548FF',
                    dates: utilsDates,
                },
                permission: 'sell.stats',
                family: 'tpv',
                modules: ['quotes']
            },
            'sell_ticket_canceled_amount': {
                component: DSBSellTicketAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_canceled_amount,
                    icon: faCalendarDays,
                    sellTicketStatus: [sellTicketStatusInversed['canceled']],
                    color: '#C548FF',
                    dates: utilsDates,
                },
                permission: 'sell.stats',
                family: 'tpv',
                modules: ['quotes']
            },
            'sell_ticket_refunded_amount': {
                component: DSBSellTicketAmount,
                className: 'small_padding_widget',
                cols: 1,
                rows: 1,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_refunded_amount,
                    icon: faCalendarDays,
                    sellTicketStatus: [sellTicketStatusInversed['refunded']],
                    color: '#C548FF',
                    dates: utilsDates,
                },
                permission: 'sell.stats',
                family: 'tpv',
                modules: ['quotes']
            },
            'sell_ticket_multiple_chart_data': {
                component: DSBSellTicketMultipleChartData,
                cols: 2,
                rows: 3,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_multiplechart_data,
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'sell.stats',
                family: 'tpv',
                modules: ['quotes']
            },
            'sell_ticket_accepted_sellers': {
                component: DSBSellTicketSellers,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_accepted_seller,
                    sellTicketStatus: [sellTicketStatusInversed['accepted']],
                    start_date: utilsDates.yearStart,
                    end_date: utilsDates.yearEnd
                },
                permission: 'sell.stats',
                family: 'tpv',
                modules: ['quotes']
            },
            'sell_ticket_days_totalvsaccepted': {
                component: DSBSellTicketTotalVsAcceptedDays,
                cols: 2,
                rows: 3,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_days_totalvsaccepted,
                    start_date: utilsDates.monthThree,
                    end_date: utilsDates.todayEnd
                },
                family: 'tpv',
                permission: 'sell.stats',
                modules: ['quotes']
            },
            'sell_ticket_status_progress_bar': {
                component: DSBSellTicketStatusProgressBar,
                cols: 2,
                rows: 2,
                extraProps: {
                    title: DSBWidgetTitlesTranslated.sell_ticket_status_progress_bar,
                    start_date: utilsDates.monthStart,
                    end_date: utilsDates.monthEnd
                },
                family: 'tpv',
                permission: 'sell.stats',
                modules: ['quotes']
            },
            'my_campaign_todays_campaigns': {
                component: DSBMyCampaignsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_campaign_todays_campaigns,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd
                },
                permission: 'customer.campaigns.inner',
                modules: ['tasks']
            },
            'my_campaign_weeks_campaigns': {
                component: DSBMyCampaignsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_campaign_weeks_campaigns,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd
                },
                permission: 'customer.campaigns.inner',
                modules: ['tasks']
            },
            'campaign_todays_campaigns': {
                component: DSBAllCampaignsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.campaign_todays_campaigns,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd
                },
                permission: 'customer.campaigns.inner',
                modules: ['tasks']
            },
            'campaign_weeks_campaigns': {
                component: DSBAllCampaignsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.campaign_weeks_campaigns,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd
                },
                permission: 'customer.campaigns.inner',
                modules: ['tasks']
            },
            'my_customer_projects_todays': {
                component: DSBMyCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_customer_project_todays_customer_projects,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd,
                    projectType: projectTypesInversed['project']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            },
            'my_customer_projects_weeks': {
                component: DSBMyCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_customer_project_weeks_customer_projects,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd,
                    projectType: projectTypesInversed['project']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            },
            'customer_project_todays': {
                component: DSBAllCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.customer_project_todays_customer_projects,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd,
                    projectType: projectTypesInversed['project']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            },
            'customer_project_weeks': {
                component: DSBAllCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.customer_project_weeks_customer_projects,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd,
                    projectType: projectTypesInversed['project']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            }, 
            'my_customer_opportunities_todays': {
                component: DSBMyCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_customer_opportunity_todays_customer_opportunities,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd,
                    projectType: projectTypesInversed['opportunity']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            },
            'my_customer_opportunities_weeks': {
                component: DSBMyCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.my_customer_opportunity_weeks_customer_opportunities,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd,
                    projectType: projectTypesInversed['opportunity']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            },
            'customer_opportunity_todays': {
                component: DSBAllCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.customer_opportunity_todays_customer_opportunities,
                    start_date: utilsDates.todayStart,
                    end_date: utilsDates.todayEnd,
                    projectType: projectTypesInversed['opportunity']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            },
            'customer_opportunity_weeks': {
                component: DSBAllCustomerProjectsTable,
                cols: 2,
                rows: 2,
                family: 'task',
                extraProps: {
                    title: DSBWidgetTitlesTranslated.customer_opportunity_weeks_customer_opportunities,
                    start_date: utilsDates.weekStart,
                    end_date: utilsDates.weekEnd,
                    projectType: projectTypesInversed['opportunity']
                },
                permission: 'customer.projects.main',
                modules: ['tasks']
            } 
        })
    }, [DSBWidgetTitlesTranslatedParsed])


    const getUserWidgets = useCallback(async () => {
        const dashboard_distribution = getEmployeeParams()?.[name]
        const params = dashboard_distribution ? JSON.parse(dashboard_distribution) : JSON.parse(defaultWidgets)
        setUserDashboardWidgets(filterUserWidgetsByAvaliableWidgets(avaliableDashboardWidgets, params))
    }, [avaliableDashboardWidgets, setUserDashboardWidgets, defaultWidgets, name])

    const saveUserWidgets = useCallback(async () => {
        const myName = name
        const myValue = JSON.stringify(userDashboardWidgets)
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValue })
        changeEmployeeParams({ [myName]: myValue })
    }, [userDashboardWidgets, name])

    useEffect(() => {
        getUserWidgets()
    }, [getUserWidgets])

    return { avaliableDashboardWidgets, userDashboardWidgets, setUserDashboardWidgets, getUserWidgets, saveUserWidgets }
}

export const filterWidgetsByPermissions = (widgets: WidgetDictionary) => {
    const userPermissions = getAllGroupPermissions()
    const modulesBuyed = getModulesBuyed()
    let widgetsFiltered: WidgetDictionary = {}
    if (userPermissions) {
        for (const key in widgets) {
            const widget = widgets[key]
            if (haveModuleBuyed(widget.modules, modulesBuyed)) {
                if (!widget.permission) {
                    widgetsFiltered[key] = { ...widget, userPermission: { n: 4, permission: 'delete' } }
                    continue
                }
                const userPermission = getMaxPermissionByGroupPermissions(widget.permission, userPermissions)
                if (userPermission.permission) {
                    widgetsFiltered[key] = { ...widget, userPermission }
                }
            }
        }
    }
    return widgetsFiltered
}

export const filterUserWidgetsByAvaliableWidgets = (widgets: WidgetDictionary, userWidgets: UserWidget[]) => {
    let widgetsFiltered: UserWidget[] = []
    for (const widget of userWidgets) {
        if (widget.name in widgets) {
            widgetsFiltered.push(widget)
        }
    }
    return widgetsFiltered
}


export default useDashboardWidgets